/**
 * Constantes y helpers compartidos del wizard de producto personalizado
 * (Dialog productivo + Preview sandbox).
 */

/** GET /configurador/arbol/ 403: no fingir catálogo vacío. */
export const MSG_CONFIGURADOR_CATALOG_FORBIDDEN =
  "No tiene permisos para cargar el catálogo del configurador.";

export const MSG_CONFIGURADOR_CATALOG_EMPTY =
  "No hay campos en el catálogo del configurador. Administralos en la pestaña Configurador.";

export const KEY_FABRICA = "fabrica_proveedor";
export const KEY_PRECIO = "precio";
export const KEY_IVA = "iva";
export const KEY_PRECIO_FINAL = "precio_final_con_iva";
export const KEY_OBS = "observacion_fina";
export const KEY_STOCK = "stock_inicial";
export const KEY_SUCURSAL = "sucursal_deposito";
export const KEY_PROVEEDOR_OTRAS = "proveedor_otras_fabricas";

/** Campos manejados fuera de los dropdowns técnicos encadenados. */
export const SPECIAL_KEYS = new Set([
  KEY_FABRICA,
  KEY_PROVEEDOR_OTRAS,
  KEY_PRECIO,
  KEY_IVA,
  KEY_PRECIO_FINAL,
  KEY_OBS,
  KEY_STOCK,
  KEY_SUCURSAL,
]);

/** Alineado con BE DESCRIPCION_MAX_LEN / DESCRIPCION_FIELD_SEPARATOR. */
export const DESCRIPCION_MAX_LEN = 1000;
export const DESCRIPCION_FIELD_SEPARATOR = " | ";

/** Un campo de la descripción: «Ancho 120» si el flag está on; si no, solo el valor. */
export function formatDescripcionPart(
  value: string,
  campoLabel?: string | null,
  mostrarEtiqueta?: boolean,
): string {
  const valueS = String(value ?? "").trim();
  if (!valueS) return "";
  if (mostrarEtiqueta) {
    const labelS = String(campoLabel ?? "").trim();
    if (labelS) return `${labelS} ${valueS}`;
  }
  return valueS;
}

/** Junta cabeza + campos + observación. Nunca « · » (parece decimal). */
export function joinDescripcionParts(
  parts: Array<string | null | undefined>,
  maxLen = DESCRIPCION_MAX_LEN,
): string {
  return parts
    .map((p) => String(p ?? "").trim())
    .filter(Boolean)
    .join(DESCRIPCION_FIELD_SEPARATOR)
    .slice(0, maxLen);
}

/** Valor visible de una selección del wizard (opción.label o texto/manual). */
export function descripcionDisplayValue(
  raw: unknown,
  opciones?: { key: string; label: string }[] | null,
): string {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") {
    const opt = (opciones || []).find((o) => o.key === raw);
    return String(opt?.label || raw).trim();
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (obj.manual != null && obj.manual !== "") {
      return String(obj.manual).trim();
    }
    const ok = obj.opcion_key ?? obj.key;
    if (ok != null && ok !== "") {
      const key = String(ok);
      const opt = (opciones || []).find((o) => o.key === key);
      return String(opt?.label || key).trim();
    }
  }
  return "";
}

export type EvalCampo = {
  key: string;
  label?: string;
  tipo?: string;
  visible?: boolean;
  enabled?: boolean;
  applicable?: boolean;
  aplicable?: boolean;
  required?: boolean;
  manualPermitido?: boolean;
  manual_permitido?: boolean;
  computed?: string | null;
  valorFijo?: string | null;
  valor_fijo?: string | null;
  enGrafo?: boolean;
  en_grafo?: boolean;
};

export type ConfiguradorProductoFormMode = "create" | "preview";

export function asEvalCampos(raw: unknown[]): EvalCampo[] {
  return (raw || []).map((c) => c as EvalCampo);
}

export function campoVisible(c: EvalCampo): boolean {
  return Boolean(c.visible || c.enabled);
}

export function campoRequired(c: EvalCampo): boolean {
  return Boolean(c.required);
}

export function campoManualOk(c: EvalCampo): boolean {
  return Boolean(c.manualPermitido ?? c.manual_permitido);
}

/** Nombre canónico FAVRE (trim + espacios colapsados), alineado al BE. */
export function isFavreProveedorNombre(
  nombre: string | null | undefined,
): boolean {
  return (nombre || "").trim().replace(/\s+/g, " ").toLowerCase() === "favre";
}

/** Combo Otras: proveedores que no son FAVRE (el perfil ya es Otras). */
export function proveedoresParaOtrasFabricas<
  T extends { nombreRazonSocial?: string | null },
>(proveedores: T[]): T[] {
  return proveedores.filter((p) => !isFavreProveedorNombre(p.nombreRazonSocial));
}

export function formatMoney(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Slug interno desde label (opción / campo). */
export function slugFromLabel(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .slice(0, 64);
}

/** Genera key única entre un set de keys existentes. */
export function uniqueSlug(base: string, existing: Set<string>, fallback = "item"): string {
  const root = base || fallback;
  if (!existing.has(root)) return root;
  let n = 2;
  while (existing.has(`${root}_${n}`)) n += 1;
  return `${root}_${n}`;
}
